import * as React from 'react';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';
import { connect } from 'react-redux';
import AppHistory from '__common/modules/history';
import { parseDate } from '__common/calculations/dateFormat';
import { products, getProductName } from '__common/constants/products';
import { getFormattedProductName } from '__common/constants/products_titles';
import NewProjectModal, { SYSTEM_TYPES } from 'v3/dashboard/NewProjectModal';
import V3Nav from 'v3/components/V3Nav';
import V3AvatarMenu from 'v3/components/V3AvatarMenu';
import V3Button from 'v3/components/V3Button';
import {
  SortChevronIcon, SearchIcon, FilterIcon,
  CopyIcon, DeleteIcon, PaginationPrevIcon, PaginationNextIcon,
  NewProjectPlusIcon,
} from 'v3/dashboard/icons';
import {
  GET_USER_PROJECTS,
  INIT_USER_PROJECTS,
  SET_YOUR_PROJECTS_PAGINATION_START,
  SET_YOUR_PROJECTS_SORTING,
  SET_YOUR_PROJECTS_TABLE_PAGE,
  DELETE_PROJECT,
  COPY_PROJECT,
  YOUR_PROJECTS_LOAD_PREFERENCES,
  SET_ROWS_PER_PAGE,
  APPLY_FILTERS,
  FETCH_MFGS_AND_MODELS_REQUEST,
} from 'actions';
import { CLEAR_YOUR_PROJECT } from 'yourProjects/yourProjectsActions';
import { OPEN_CONFIRM_CLEAR_ARRAYS_MODAL } from 'actions';
import { showSuccessAlert } from '__common/modules/alerts';
import { getLiteProductFilterOptions, getUbuilderLiteProducts } from 'app/components/HeaderComponent/utils/getProducts';

type ProjectTableData = {
  data: any[] | null;
  rowsPerPage: number;
  totalProjects: number;
  paginationStart: number;
  paginationTotal: number;
  paginationPage: number;
  sortedField: string;
  sortMethod: string;
  filterString: string;
  showOldProjects: boolean;
  areLoading: boolean;
  appliedFilters: {};
};

type UserPreferences = {
  projects_per_page: number;
  project_sorting: number;
  sort_method: number;
};

type SortCol = 'project_name' | 'client_name' | 'created' | 'panel_mfg' | 'panel_model' | 'zipcode' | 'total_price';

type Props = {
  user: userState;
  dispatch: Function;
  projectTableData: ProjectTableData;
  userPreferences: UserPreferences;
  mfgs: mfg[];
};

type State = {
  newProjectOpen: boolean;
  searchQuery: string;
  filterPanelOpen: boolean;
  filterMfg: string;
  filterSystem: string;
  filterStates: string[];
  stateScrollOffset: number;
  stateDropdownOpen: boolean;
  rowsDropdownOpen: boolean;
};

const SORT_COLUMNS = ['project_name', 'created', 'panel_mfg', 'panel_model', 'client_name', 'address', 'zipcode', 'total_price'];

const DEFAULT_ROWS_PER_PAGE = 10;

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

const MAX_VISIBLE_STATES = 9;

const DEFAULT_SORT_FIELD = 'created';

const DEFAULT_SORT_METHOD = 'asc';

const STATE_OPTIONS = ['AK', 'AL', 'AR', 'AS', 'AZ', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL', 'GA', 'GU', 'HI', 'IA', 'ID', 'IL', 'IN', 'KS', 'KY',
  'LA', 'MA', 'MD', 'ME', 'MI', 'MN', 'MO', 'MP', 'MS', 'MT', 'NA', 'NC', 'ND', 'NE', 'NH', 'NJ', 'NM', 'NV', 'NY', 'OH', 'OK', 'OR', 'PA',
  'PR', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VA', 'VI', 'VT', 'WA', 'WI', 'WV', 'WY'];

const LITE_SYSTEM_PRODUCT_GROUPS: { [key: number]: number[] } = {
  [products.nxt_umount]: [products.nxt_umount, products.nxt_horizon_hd],
  [products.solarmount_2]: [products.solarmount_2, products.solarmount_lt, products.solarmount_ascender_flush],
};


class LiteDashboard extends React.Component<Props, State> {
  private throttledCopyProject: (projectId: string) => void;
  private throttledDeleteProject: (projectId: string) => void;
  private debouncedSearch: (value: string) => void;
  private rowsDropdownRef = React.createRef<HTMLDivElement>();
  private stateDropdownRef = React.createRef<HTMLDivElement>();

  state: State = {
    newProjectOpen: false,
    searchQuery: '',
    filterPanelOpen: false,
    filterMfg: '',
    filterSystem: '',
    filterStates: [],
    stateScrollOffset: 0,
    stateDropdownOpen: false,
    rowsDropdownOpen: false,
  };

  constructor(props: Props) {
    super(props);
    this.throttledCopyProject = throttle(this.copyProject, 1000);
    this.throttledDeleteProject = throttle(this.deleteProject, 1000);
    this.debouncedSearch = debounce(this.executeSearch, 800);
  }

  componentDidMount() {
    this.loadUserPreferences();
    const { dispatch } = this.props;
    dispatch(GET_USER_PROJECTS(
      DEFAULT_ROWS_PER_PAGE,
      0,
      DEFAULT_SORT_FIELD,
      DEFAULT_SORT_METHOD,
      '',
      false,
      { product: getUbuilderLiteProducts(), application: 'ubuilder_lite' },
    ))
    dispatch(FETCH_MFGS_AND_MODELS_REQUEST('', '', '', ''));
    document.addEventListener('mousedown', this.handleDocumentClick);
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    dispatch(CLEAR_YOUR_PROJECT());
    document.removeEventListener('mousedown', this.handleDocumentClick);
  }

  handleDocumentClick = (e: MouseEvent) => {
    const target = e.target as Node;
    const rowsNode = this.rowsDropdownRef.current;
    if (rowsNode && !rowsNode.contains(target) && this.state.rowsDropdownOpen) {
      this.setState({ rowsDropdownOpen: false });
    }
    const stateNode = this.stateDropdownRef.current;
    if (stateNode && !stateNode.contains(target) && this.state.stateDropdownOpen) {
      this.setState({ stateDropdownOpen: false });
    }
  };

  toggleRowsDropdown = () => {
    this.setState((prev) => ({ rowsDropdownOpen: !prev.rowsDropdownOpen }));
  };

  selectRowsOption = (rowsPerPage: number) => {
    const { dispatch } = this.props;
    this.setState({ rowsDropdownOpen: false });
    dispatch(SET_ROWS_PER_PAGE(rowsPerPage));
    dispatch(SET_YOUR_PROJECTS_PAGINATION_START(0));
    dispatch(SET_YOUR_PROJECTS_TABLE_PAGE(1));
    this.fetchProjects(
      rowsPerPage,
      0,
      undefined,
      undefined,
      this.getEffectiveSearchQuery(),
      this.buildAppliedFilters(),
    );
  };

  loadUserPreferences() {
    const { dispatch, userPreferences } = this.props;
    const sortField = SORT_COLUMNS[userPreferences.project_sorting - 1] || 'project_name';
    const sortDir = userPreferences.sort_method === 1 ? 'desc' : 'asc';
    const savedPerPage = userPreferences.projects_per_page;
    const perPage = (savedPerPage && ROWS_PER_PAGE_OPTIONS.indexOf(savedPerPage) !== -1)
      ? savedPerPage
      : DEFAULT_ROWS_PER_PAGE;
    dispatch(YOUR_PROJECTS_LOAD_PREFERENCES(sortField, sortDir, perPage));
  }

  openNewProject = () => this.setState({ newProjectOpen: true });
  closeNewProject = () => this.setState({ newProjectOpen: false });

  buildAppliedFilters() {
    const { filterMfg, filterSystem, filterStates } = this.state;
    const applied: { module?: number[]; product?: number[]; project_state?: string[]; application?: string } = { application: 'ubuilder_lite' };
    if (filterMfg) {
      applied.module = [Number(filterMfg)];
    }
    if (filterSystem) {
      const selectedId = Number(filterSystem);
      applied.product = LITE_SYSTEM_PRODUCT_GROUPS[selectedId] || [selectedId];
    }
    if (filterStates.length > 0) {
      applied.project_state = filterStates;
    }
    return applied;
  }

  getEffectiveSearchQuery() {
    const { searchQuery } = this.state;
    return searchQuery.length >= 3 ? searchQuery : '';
  }

  fetchProjects(
    rowsPerPage?: number,
    paginationStart?: number,
    sortedField?: string,
    sortMethod?: string,
    filterString?: string,
    appliedFilters?: {},
  ) {
    const { dispatch, projectTableData } = this.props;
    dispatch(
      GET_USER_PROJECTS(
        rowsPerPage ?? projectTableData.rowsPerPage,
        paginationStart ?? projectTableData.paginationStart,
        sortedField ?? projectTableData.sortedField,
        sortMethod ?? projectTableData.sortMethod,
        filterString ?? projectTableData.filterString,
        projectTableData.showOldProjects,
        appliedFilters ?? this.buildAppliedFilters(),
      ),
    );
  }

  executeSearch = (value: string) => {
    if (value.length > 0 && value.length < 3) {
      return;
    }
    const { projectTableData } = this.props;
    this.fetchProjects(
      projectTableData.rowsPerPage,
      0,
      projectTableData.sortedField,
      projectTableData.sortMethod,
      value,
      this.buildAppliedFilters(),
    );
  };

  onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    this.setState({ searchQuery: value });
    this.debouncedSearch(value);
  };

  toggleFilters = () => {
    this.setState((prev) => ({ filterPanelOpen: !prev.filterPanelOpen }));
  };

  clearFilters = () => {
    this.setState(
      {
        filterMfg: '',
        filterSystem: '',
        filterStates: [],
        stateScrollOffset: 0,
        searchQuery: '',
      },
      () => {
        const { dispatch } = this.props;
        const appliedFilters = { application: 'ubuilder_lite' };
        dispatch(APPLY_FILTERS(appliedFilters));
        dispatch(SET_YOUR_PROJECTS_PAGINATION_START(0));
        dispatch(SET_YOUR_PROJECTS_TABLE_PAGE(1));
        this.fetchProjects(
          undefined,
          0,
          undefined,
          undefined,
          '',
          appliedFilters,
        );
      },
    );
  };

  onFilterChange = (field: 'filterMfg' | 'filterSystem') => (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    this.setState({ [field]: e.target.value } as Pick<State, typeof field>, this.applyFilters);
  };

  toggleStateDropdown = () => {
    this.setState((prev) => ({ stateDropdownOpen: !prev.stateDropdownOpen }));
  };

  addState = (code: string) => {
    const { filterStates } = this.state;
    if (filterStates.indexOf(code) !== -1) {
      this.setState({ stateDropdownOpen: false });
      return;
    }
    this.setState(
      { filterStates: [...filterStates, code].sort(), stateDropdownOpen: false },
      this.applyFilters,
    );
  };

  removeState = (code: string) => {
    const { filterStates, stateScrollOffset } = this.state;
    const next = filterStates.filter((s) => s !== code);
    const maxOffset = Math.max(0, next.length - MAX_VISIBLE_STATES);
    this.setState(
      { filterStates: next, stateScrollOffset: Math.min(stateScrollOffset, maxOffset) },
      this.applyFilters,
    );
  };

  clearStates = () => {
    this.setState({ filterStates: [], stateScrollOffset: 0 }, this.applyFilters);
  };

  scrollStates = (direction: 'prev' | 'next') => {
    const { filterStates, stateScrollOffset } = this.state;
    const maxOffset = Math.max(0, filterStates.length - MAX_VISIBLE_STATES);
    const delta = direction === 'next' ? 1 : -1;
    const nextOffset = Math.min(maxOffset, Math.max(0, stateScrollOffset + delta));
    if (nextOffset !== stateScrollOffset) {
      this.setState({ stateScrollOffset: nextOffset });
    }
  };

  applyFilters = () => {
    const { dispatch, projectTableData } = this.props;
    const appliedFilters = this.buildAppliedFilters();
    dispatch(APPLY_FILTERS(appliedFilters));
    dispatch(SET_YOUR_PROJECTS_PAGINATION_START(0));
    dispatch(SET_YOUR_PROJECTS_TABLE_PAGE(1));
    this.fetchProjects(
      projectTableData.rowsPerPage,
      0,
      projectTableData.sortedField,
      projectTableData.sortMethod,
      this.getEffectiveSearchQuery(),
      appliedFilters,
    );
  };

  onSort = (col: SortCol) => {
    const { dispatch, projectTableData } = this.props;
    let newSortMethod: 'asc' | 'desc';

    if (projectTableData.sortedField === col) {
      newSortMethod = projectTableData.sortMethod === 'asc' ? 'desc' : 'asc';
    } else {
      newSortMethod = 'asc';
    }

    dispatch(SET_YOUR_PROJECTS_SORTING(col, newSortMethod));
    this.fetchProjects(
      projectTableData.rowsPerPage,
      projectTableData.paginationStart,
      col,
      newSortMethod,
      this.getEffectiveSearchQuery(),
      this.buildAppliedFilters(),
    );
  };

  handlePagination = (direction: 'prev' | 'next') => {
    const { dispatch, projectTableData } = this.props;
    const { rowsPerPage, paginationStart, paginationPage } = projectTableData;

    let newStart: number;
    let newPage: number;

    if (direction === 'next') {
      newStart = paginationStart + rowsPerPage;
      newPage = paginationPage + 1;
    } else {
      newStart = Math.max(0, paginationStart - rowsPerPage);
      newPage = Math.max(1, paginationPage - 1);
    }

    dispatch(SET_YOUR_PROJECTS_PAGINATION_START(newStart));
    dispatch(SET_YOUR_PROJECTS_TABLE_PAGE(newPage));
    this.fetchProjects(
      rowsPerPage,
      newStart,
      undefined,
      undefined,
      this.getEffectiveSearchQuery(),
      this.buildAppliedFilters(),
    );
  };

  // Skip SEARCH_PROJECT / REDIRECT_TO_PROJECT — those route to the non-Lite
  // `/project/design/:productName/:projectId` and open a new tab. The Lite
  // dashboard row already has product_id + project_id, so push directly.
  goToProject = (projectId: string, productId: number) => {
    const productName = getProductName(productId);
    if (!productName) return;
    AppHistory.push(`/project/lite/design/${productName}/${projectId}`);
  };

  copyProject = (projectId: string) => {
    const { dispatch } = this.props;
    dispatch(COPY_PROJECT(projectId, true));
  };

  deleteProject = (projectId: string) => {
    const { dispatch, projectTableData } = this.props;
    const {
      rowsPerPage,
      paginationStart,
      sortedField,
      sortMethod,
      showOldProjects,
    } = projectTableData;

    const appliedFilters = this.buildAppliedFilters();
    const filterString = this.getEffectiveSearchQuery();

    const postDeleteAction = GET_USER_PROJECTS.bind(
      null,
      rowsPerPage,
      paginationStart,
      sortedField,
      sortMethod,
      filterString,
      showOldProjects,
      appliedFilters,
    );

    dispatch(
      OPEN_CONFIRM_CLEAR_ARRAYS_MODAL(
        'Are you sure you want to delete the project?',
        'All project details will be lost once deleted.',
        () => {
          dispatch(DELETE_PROJECT(postDeleteAction, projectId));
          showSuccessAlert('Project deleted successfully');
        },
      ),
    );
  };

  renderSortableHeader(col: SortCol, label: string) {
    const { projectTableData } = this.props;
    const isActive = projectTableData.sortedField === col;
    const cls = [
      'sortable',
      isActive ? 'sort-active' : '',
      isActive ? (projectTableData.sortMethod === 'asc' ? 'sort-asc' : 'sort-desc') : '',
    ].filter(Boolean).join(' ');

    return (
      <th className={cls} data-col={col} onClick={() => this.onSort(col)}>
        <div className="th-inner">
          {label}
          <SortChevronIcon />
        </div>
      </th>
    );
  }

  renderProjectRow = (project: any, index: number) => {
    return (
      <tr key={project.project_id || index} data-id={project.project_id}>
        <td>
          <div className="project-name">{project.project_name}</div>
          <div className="project-pid">#{project.project_id ? project.project_id.toUpperCase() : ''}</div>
        </td>
        <td style={{ color: 'var(--t2)', fontSize: 13 }}>{project.client_name}</td>
        <td style={{ color: 'var(--t3)', fontSize: 12, whiteSpace: 'nowrap' }}>{parseDate(project.created)}</td>
        <td style={{ fontSize: 12, color: 'var(--t2)' }}>{project.panel_mfg}</td>
        <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: 'var(--t2)', maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {project.panel_model}
        </td>
        <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: 'var(--t2)' }}>{project.zipcode}</td>
        <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: 'var(--t1)' }}>
          {project.total_price || 'N/A'}
        </td>
        <td>
          <div className="row-actions">
            <V3Button variant="primary" size="sm" onClick={() => this.goToProject(project.project_id, project.product_id)}>
              Open →
            </V3Button>
            <button
              className="icon-btn copy"
              title="Copy project"
              onClick={() => this.throttledCopyProject(project.project_id)}
            >
              <CopyIcon />
            </button>
            <button
              className="icon-btn del"
              title="Delete project"
              onClick={() => this.throttledDeleteProject(project.project_id)}
            >
              <DeleteIcon />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  renderPagination() {
    const { projectTableData } = this.props;
    const { paginationStart, rowsPerPage, paginationTotal } = projectTableData;

    const currentEnd = Math.min(paginationStart + rowsPerPage, paginationTotal);
    const hasPrev = paginationStart > 0;
    const hasNext = currentEnd < paginationTotal;

    return (
      <div className="v3-pagination-footer">
        <span>{paginationStart + 1}–{currentEnd} of {paginationTotal}</span>
        <button
          className="v3-pagination-nav-btn"
          disabled={!hasPrev}
          onClick={() => this.handlePagination('prev')}
        >
          <PaginationPrevIcon />
        </button>
        <button
          className="v3-pagination-nav-btn"
          disabled={!hasNext}
          onClick={() => this.handlePagination('next')}
        >
          <PaginationNextIcon />
        </button>
      </div>
    );
  }

  renderStateMultiSelect() {
    const { filterStates, stateScrollOffset, stateDropdownOpen } = this.state;
    const hasMany = filterStates.length > MAX_VISIBLE_STATES;
    const maxOffset = Math.max(0, filterStates.length - MAX_VISIBLE_STATES);
    const visibleStates = hasMany
      ? filterStates.slice(stateScrollOffset, stateScrollOffset + MAX_VISIBLE_STATES)
      : filterStates;
    const availableStates = STATE_OPTIONS.filter((s) => filterStates.indexOf(s) === -1);
    const stopAndRun = (fn: () => void) => (e: React.MouseEvent) => {
      e.stopPropagation();
      fn();
    };

    return (
      <div className="state-multi-anchor" ref={this.stateDropdownRef}>
        <div
          className={`state-multi${stateDropdownOpen ? ' open' : ''}`}
          onClick={this.toggleStateDropdown}
          role="button"
          tabIndex={0}
        >
          {hasMany && (
            <button
              type="button"
              className="state-arrow"
              disabled={stateScrollOffset === 0}
              onClick={stopAndRun(() => this.scrollStates('prev'))}
              aria-label="Scroll previous states"
            >
              <svg width="6" height="9" viewBox="0 0 7 11" fill="none">
                <path d="M6 1L1 5.5L6 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
          <div className="state-multi-viewport">
            {filterStates.length === 0 && (
              <span className="state-multi-placeholder">All States</span>
            )}
            {visibleStates.map((code) => (
              <span key={code} className="state-chip">
                {code}
                <button
                  type="button"
                  className="state-chip-x"
                  onClick={stopAndRun(() => this.removeState(code))}
                  aria-label={`Remove ${code}`}
                >
                  <svg width="7" height="7" viewBox="0 0 8 8" fill="none">
                    <path d="M1 1l6 6M7 1L1 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
          {hasMany && (
            <button
              type="button"
              className="state-arrow"
              disabled={stateScrollOffset >= maxOffset}
              onClick={stopAndRun(() => this.scrollStates('next'))}
              aria-label="Scroll next states"
            >
              <svg width="6" height="9" viewBox="0 0 7 11" fill="none">
                <path d="M1 1L6 5.5L1 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
          {filterStates.length > 0 && (
            <button
              type="button"
              className="state-clear-x"
              onClick={stopAndRun(this.clearStates)}
              aria-label="Clear all selected states"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          )}
          <span className="state-multi-chevron">
            <svg width="10" height="7" viewBox="0 0 10 7" fill="none">
              <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </span>
        </div>
        {stateDropdownOpen && (
          <div className="state-multi-menu">
            {availableStates.length === 0 ? (
              <div className="state-multi-empty">All states selected</div>
            ) : (
              availableStates.map((s) => (
                <div
                  key={s}
                  className="state-multi-opt"
                  onClick={() => this.addState(s)}
                >
                  {s}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  }

  renderProjectList() {
    const { projectTableData, mfgs } = this.props;
    const { searchQuery, filterPanelOpen, filterMfg, filterSystem, filterStates, rowsDropdownOpen } = this.state;
    const data = projectTableData.data || [];
    const anyFilter = !!(filterMfg || filterSystem || filterStates.length > 0);
    const resultCountText = anyFilter || searchQuery
      ? `${projectTableData.paginationTotal} of ${projectTableData.totalProjects} projects`
      : `${projectTableData.paginationTotal} projects`;
    const mfgOptions = (mfgs || [])
      .filter(({ name }) => name !== 'All')
      .map(({ id, name }) => ({ value: id, label: name }));
    const productOptions = SYSTEM_TYPES.map((system) => {
      const productId = products[system.productId];
      return {
        label: getFormattedProductName(productId),
        value: productId,
      };
    });

    return (
      <>
        <div className="section-header">
          <div className="section-title">Recent Projects</div>
        </div>

        <div className="projects-toolbar">
          <div className="search-wrap">
            <span className="search-icon">
              <SearchIcon />
            </span>
            <input
              className="search-input"
              type="text"
              placeholder="Search projects or clients…"
              value={searchQuery}
              onChange={this.onSearchChange}
            />
          </div>
          <button
            className={`filter-btn${anyFilter ? ' active' : ''}`}
            onClick={this.toggleFilters}
          >
            <FilterIcon />
            Filters
            <span className="filter-dot" />
          </button>
          <span style={{ fontSize: 12, color: 'var(--t3)', marginLeft: 4 }}>{resultCountText}</span>
          <div ref={this.rowsDropdownRef} className="v3-rows-dropdown">
            <span className="v3-rows-dropdown-label">Show</span>
            <div className="v3-rows-dropdown-anchor">
              <button
                className={`v3-rows-dropdown-btn${rowsDropdownOpen ? ' open' : ''}`}
                onClick={this.toggleRowsDropdown}
              >
                <span>{projectTableData.rowsPerPage} rows</span>
                <svg width="10" height="7" viewBox="0 0 10 7" fill="none">
                  <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
              {rowsDropdownOpen && (
                <div className="v3-rows-dropdown-menu">
                  {ROWS_PER_PAGE_OPTIONS.map((n) => {
                    const isActive = n === projectTableData.rowsPerPage;
                    return (
                      <div
                        key={n}
                        className={`v3-rows-opt${isActive ? ' active' : ''}`}
                        onClick={() => this.selectRowsOption(n)}
                      >
                        {n} rows
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {filterPanelOpen && (
          <div className="filter-panel open">
            <div className="filter-group">
              <label>MFG</label>
              <select className="filter-select" value={filterMfg} onChange={this.onFilterChange('filterMfg')}>
                <option value="">All Manufacturers</option>
                {mfgOptions.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>System</label>
              <select className="filter-select" value={filterSystem} onChange={this.onFilterChange('filterSystem')}>
                <option value="">All Systems</option>
                {productOptions.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>State</label>
              {this.renderStateMultiSelect()}
            </div>
            <button className="filter-clear" onClick={this.clearFilters}>Clear all</button>
          </div>
        )}

        <div className="projects-card">
          {projectTableData.areLoading ? (
            <div className="no-results">
              <strong>Loading projects…</strong>
            </div>
          ) : (
            <>
              <table className="projects-table">
                <thead>
                  <tr>
                    {this.renderSortableHeader('project_name', 'Project Name')}
                    {this.renderSortableHeader('client_name', 'Client Name')}
                    {this.renderSortableHeader('created', 'Created')}
                    {this.renderSortableHeader('panel_mfg', 'MFG')}
                    {this.renderSortableHeader('panel_model', 'Model')}
                    {this.renderSortableHeader('zipcode', 'Zip')}
                    {this.renderSortableHeader('total_price', 'List')}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.map(this.renderProjectRow)}
                </tbody>
              </table>
              {data.length === 0 && (
                <div className="no-results">
                  <strong>No projects found</strong>
                  Try adjusting your search or filters.
                </div>
              )}
              {data.length > 0 && this.renderPagination()}
            </>
          )}
        </div>
      </>
    );
  }

  render() {
    const { user, dispatch } = this.props;
    const { newProjectOpen } = this.state;
    const rawName = user.username || '';
    const capitalizedName = rawName ? rawName.charAt(0).toUpperCase() + rawName.slice(1) : '';

    return (
      <div className="v3-screen">
        <V3Nav
          variant="dashboard"
          logo={
            <img
              src={require('assets/media/logos/U-Builder_Go_Logo.png')}
              alt="U-Builder"
              style={{ height: 32, width: 'auto', display: 'block' }}
            />
          }
          rightSlot={
            <React.Fragment>
              <button className="v3-new-project-btn" onClick={this.openNewProject}>
                <NewProjectPlusIcon />
                <span>New Project</span>
              </button>
              <V3AvatarMenu user={user} dispatch={dispatch} />
            </React.Fragment>
          }
        />

        <div className="lite-dashboard__body">
          <div className="lite-dashboard__hero">
            <h1>
              {capitalizedName
                ? <>Welcome back, <span className="v3-orange">{capitalizedName}</span></>
                : 'Welcome back!'}
            </h1>
            <p>Manage your solar racking designs, generate BOMs, and collaborate with your team.</p>
          </div>

          <div className="lite-dashboard__projects">
            {this.renderProjectList()}
          </div>
        </div>

        {newProjectOpen && <NewProjectModal onClose={this.closeNewProject} />}
      </div>
    );
  }
}

function mapStateToProps(appState: appState) {
  return {
    user: appState.user,
    projectTableData: appState.yourProjects,
    userPreferences: appState.userPreferences,
    mfgs: appState.moduleSelector.mfgs,
  };
}

export default connect(mapStateToProps)(LiteDashboard);
